import React, { useState, useEffect } from 'react';
import { FileCheck, ChevronDown, Eye, MessageSquare, Star, Briefcase, GraduationCap, Building2, MapPin, FileText, Download, Users, Target, Clock, CheckCircle, XCircle, AlertCircle, Sparkles, Bell } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { User } from '../types';
import { getAllOfferApprovals, approveOffer, getEvalSummary, nudgeOffer, uploadSalaryMaterials, deleteSalaryMaterial, transitionOffer } from '../api';
import { formatBeijingTime } from '../utils/dateFormat';

interface Props {
  currentUser: User;
  notificationBell?: React.ReactNode;
  onOpenCandidate?: (id: string) => void;
  initialCandidateId?: string | null;
  onConsumedInitialCandidate?: () => void;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; icon: any }> = {
  pending: { label: '待审批', color: 'text-gray-600', bg: 'bg-gray-100', icon: Clock },
  approved: { label: '已通过', color: 'text-primary-700', bg: 'bg-primary-50', icon: CheckCircle },
  rejected: { label: '已拒绝', color: 'text-white', bg: 'bg-gray-900', icon: XCircle },
};

const STEP_LABELS: Record<string, string> = { plan: '薪酬方案', pdf: 'Offer附件', node: '节点审批' };

const EMPLOYMENT_LABELS: Record<string, string> = {
  intern: '实习', fulltime: '全职', full_time: '全职', parttime: '兼职', part_time: '兼职', contractor: '外包/劳务',
};

/**
 * 结构化展示 HR 在发起 Offer 时填写的全部内容。
 * 后端已按查看者权限脱敏（no_salary / attachment_only 视图下薪酬字段被剔除），
 * 因此此处只需渲染「存在且非空」的字段，天然实现按角色差异化展示。
 */
function OfferDetailGrid({ o }: { o: any }) {
  const isIntern = o.employment_type === 'intern';
  const money = (v: any) => `${Number(v).toLocaleString('zh-CN')} 元`;
  const items: { label: string; value: string }[] = [];
  const pushNum = (label: string, v: any, fmt: (x: any) => string = money) => {
    if (v !== undefined && v !== null && v !== '' && Number(v) > 0) items.push({ label, value: fmt(v) });
  };
  const pushStr = (label: string, v: any) => {
    if (v !== undefined && v !== null && String(v).trim() !== '') items.push({ label, value: String(v) });
  };

  if (o.employment_type) items.push({ label: '用工类型', value: EMPLOYMENT_LABELS[o.employment_type] || o.employment_type });
  pushStr('工作地点', o.work_location);
  pushStr('预计入职', o.expected_onboard_date);
  pushNum('试用期', o.probation_months, (x) => `${x} 个月`);
  pushStr('试用期到期', o.probation_end_date);
  pushStr('合同到期', o.contract_end_date);
  pushStr('实习结束', o.intern_end_date);
  if (isIntern) {
    pushNum('薪酬/津贴', o.comp_amount, (x) => `${Number(x).toLocaleString('zh-CN')} ${o.comp_currency || 'RMB'}/${o.comp_unit || '月'}`);
  } else {
    pushNum('转正月薪·总额', o.comp_amount);
    pushNum('└ 基础工资', o.base_salary);
    pushNum('└ 绩效工资', o.performance_salary);
    pushNum('试用期月薪', o.probation_salary);
    pushStr('年终奖系数', o.year_end_multiplier);
    pushNum('期权参考价值', o.equity_value);
    pushNum('全勤奖', o.full_attendance_bonus);
    pushNum('岗位津贴', o.post_allowance);
    pushNum('交通津贴', o.transport_allowance);
    pushNum('技能津贴(转正前)', o.skill_allowance_pre);
    pushNum('技能津贴(转正后)', o.skill_allowance_post);
    pushNum('管理津贴', o.management_allowance);
    pushNum('房补', o.housing_allowance);
    pushNum('月度绩效', o.monthly_performance);
    pushNum('季度绩效', o.quarterly_performance);
    pushNum('半年绩效', o.half_year_performance);
    pushNum('签字费', o.sign_on_bonus);
  }
  if (o.has_noncompete) items.push({ label: '竞业协议', value: '存在正在执行的竞业协议' });

  const redactedNote = o.redacted === 'no_salary'
    ? '按您的审批权限，薪酬明细已隐藏'
    : o.redacted === 'attachment_only'
      ? '按您的审批权限，仅可查看 Offer 附件与基本信息'
      : '';

  return (
    <div className="mt-4">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 mb-2">
        <FileText size={12} className="text-primary-500" />
        Offer 内容明细
        {redactedNote && <span className="text-[10px] text-gray-400 font-normal ml-1">（{redactedNote}）</span>}
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-gray-400 italic">
          {redactedNote || '未填写结构化 Offer 内容'}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2 text-xs bg-primary-50/40 rounded-lg p-3">
          {items.map((it, i) => (
            <div key={i} className="flex flex-col">
              <span className="text-gray-400">{it.label}</span>
              <span className="text-gray-800 font-medium break-all">{it.value}</span>
            </div>
          ))}
        </div>
      )}
      {(o.perks || o.other_notes) && (
        <div className="mt-2 space-y-1.5">
          {o.perks && <div className="text-xs text-gray-600"><span className="text-gray-400">福利：</span>{o.perks}</div>}
          {o.other_notes && <div className="text-xs text-gray-600 whitespace-pre-wrap"><span className="text-gray-400">其他说明：</span>{o.other_notes}</div>}
        </div>
      )}
    </div>
  );
}

const ROUND_LABELS: Record<string, string> = {
  first_interview: '初面', second_interview: '复面', final_interview: '终面',
  screening: '筛选', screening_passed: '筛选通过',
};

const URGENCY_LABELS: Record<string, { label: string; color: string }> = {
  urgent: { label: '紧急', color: 'text-white bg-gray-900' },
  high: { label: '高', color: 'text-gray-700 bg-gray-100' },
  normal: { label: '正常', color: 'text-primary-600 bg-primary-50' },
  low: { label: '低', color: 'text-gray-500 bg-gray-100' },
};

export default function OfferApprovalPage({ currentUser, notificationBell, onOpenCandidate, initialCandidateId, onConsumedInitialCandidate }: Props) {
  const [records, setRecords] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending');
  const [approveModal, setApproveModal] = useState<{ id: string; name: string; action: 'approved' | 'rejected'; source?: string } | null>(null);
  const [comment, setComment] = useState('');
  const [processing, setProcessing] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [nudgingId, setNudgingId] = useState<string | null>(null);
  const [evalSummaries, setEvalSummaries] = useState<Record<string, string>>({});
  const [summaryLoading, setSummaryLoading] = useState<Record<string, boolean>>({});
  const [salFiles, setSalFiles] = useState<Record<string, File[]>>({});
  const [salUploading, setSalUploading] = useState<string | null>(null);
  const isHr = ['admin', 'hr'].includes(currentUser.role);
  // Offer 审批面板仅 HR/admin 与配置指定的审批人（HRD/CEO/CFO）开放；后端 offers-all 已同步 403 拦截
  const canAccessBoard = isHr || !!currentUser.is_offer_approver;

  const fetchData = () => {
    setLoading(true);
    getAllOfferApprovals().then(setRecords).catch(() => toast.error('加载失败')).finally(() => setLoading(false));
  };

  const handleSalaryUpload = async (candidateId: string) => {
    const files = salFiles[candidateId] || [];
    if (!files.length) return;
    setSalUploading(candidateId);
    try {
      await uploadSalaryMaterials(candidateId, files);
      toast.success('薪酬材料上传成功');
      setSalFiles(prev => ({ ...prev, [candidateId]: [] }));
      fetchData();
    } catch { toast.error('上传失败'); }
    finally { setSalUploading(null); }
  };

  const handleSalaryDelete = async (id: string) => {
    if (!window.confirm('确认删除该薪酬材料？')) return;
    try { await deleteSalaryMaterial(id); toast.success('已删除'); fetchData(); }
    catch { toast.error('删除失败'); }
  };

  useEffect(() => { if (canAccessBoard) fetchData(); }, []);

  const filtered = filter === 'all' ? records : records.filter(r => r.status === filter);
  const pendingCount = records.filter(r => r.status === 'pending').length;

  const handleApprove = async () => {
    if (!approveModal) return;
    setProcessing(true);
    try {
      if (approveModal.source === 'node') {
        await transitionOffer(approveModal.id, approveModal.action === 'approved' ? 'approve' : 'reject', comment);
        toast.success(approveModal.action === 'approved' ? '已通过' : '已拒绝');
      } else {
        const result = await approveOffer(approveModal.id, { status: approveModal.action, comment });
        toast.success(approveModal.action === 'approved' ? '已通过' : '已拒绝');
        if (result.auto_transitioned) {
          toast.success('候选人已自动流转至待入职', { icon: '🎉', duration: 4000 });
        }
      }
      setApproveModal(null);
      setComment('');
      fetchData();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || '操作失败');
    } finally { setProcessing(false); }
  };

  const canApprove = (record: any) => {
    if (record.status !== 'pending') return false;
    if (currentUser.role === 'admin') return true;
    try {
      const ids = JSON.parse(record.approver_user_ids || '[]');
      return ids.includes(currentUser.id);
    } catch { return false; }
  };

  const canNudge = (record: any) =>
    record.source !== 'node' && record.status === 'pending'
    && (record.submitted_by === currentUser.id || ['admin', 'hr'].includes(currentUser.role));

  const handleNudge = async (id: string) => {
    setNudgingId(id);
    try {
      const res = await nudgeOffer(id);
      toast.success(res?.message || '已催办');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || '催办失败');
    } finally {
      setNudgingId(null);
    }
  };

  const fetchEvalSummary = async (candidateId: string) => {
    if (evalSummaries[candidateId] || summaryLoading[candidateId]) return;
    setSummaryLoading(prev => ({ ...prev, [candidateId]: true }));
    try {
      const { summary } = await getEvalSummary(candidateId);
      setEvalSummaries(prev => ({ ...prev, [candidateId]: summary }));
    } catch {
      setEvalSummaries(prev => ({ ...prev, [candidateId]: '（摘要加载失败）' }));
    } finally {
      setSummaryLoading(prev => ({ ...prev, [candidateId]: false }));
    }
  };

  // Compute average rating from evaluations
  const avgRating = (evals: any[]) => {
    const rated = evals.filter(e => e.rating > 0);
    if (rated.length === 0) return 0;
    return rated.reduce((sum, e) => sum + e.rating, 0) / rated.length;
  };

  // 从飞书/站内信「?module=offer_approval&candidate=<id>」进入时，自动展开对应候选人的工单
  useEffect(() => {
    if (!initialCandidateId || records.length === 0) return;
    const rec = records.find(r => r.candidate_id === initialCandidateId && r.status === 'pending')
      || records.find(r => r.candidate_id === initialCandidateId);
    if (rec) {
      setExpandedId(rec.id);
      // 默认停在「待审批」时，直达链接指向的已处理工单不在列表里，自动切到「全部」保证可见
      if (rec.status !== 'pending') setFilter('all');
      if ((rec.evaluations || []).length > 0) fetchEvalSummary(rec.candidate_id);
    }
    onConsumedInitialCandidate?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCandidateId, records]);

  if (!canAccessBoard) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-white rounded-2xl border border-gray-200/80 text-gray-400">
        <FileCheck size={40} strokeWidth={1} className="text-gray-300 mb-3" />
        <p className="text-sm font-medium text-gray-500">Offer 审批面板仅 HR 与审批人（CEO / CFO）开放</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col gap-2.5">
      {/* Header */}
      <div className="shrink-0 bg-white rounded-2xl border border-gray-200/80 px-6 py-3 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <FileCheck size={20} className="text-primary-500" />
            Offer决策看板
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            集中审批薪酬方案和Offer · {pendingCount > 0 ? `${pendingCount} 条待审批` : '暂无待审批'}
          </p>
        </div>
        {notificationBell}
      </div>

      {/* Filter tabs */}
      <div className="shrink-0 bg-white rounded-2xl border border-gray-200/80 px-6 py-2 flex items-center gap-1">
        {([['pending', '待审批'], ['all', '全部'], ['approved', '已通过'], ['rejected', '已拒绝']] as const).map(([key, label]) => (
          <button key={key} onClick={() => setFilter(key)}
            className={clsx('px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
              filter === key ? 'bg-primary-50 text-primary-700' : 'text-gray-500 hover:bg-gray-50')}>
            {label}
            {key === 'pending' && pendingCount > 0 && (
              <span className="ml-1 bg-primary-600 text-white text-[10px] px-1.5 py-0.5 rounded-full">{pendingCount}</span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
          <FileCheck size={48} strokeWidth={1} className="text-gray-300 mb-3" />
          <p className="text-sm">{filter === 'all' ? '暂无Offer审批记录' : `暂无${STATUS_CONFIG[filter]?.label || ''}记录`}</p>
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="space-y-2.5">
            {filtered.map(r => {
              const sc = STATUS_CONFIG[r.status] || STATUS_CONFIG.pending;
              const StatusIcon = sc.icon;
              const expanded = expandedId === r.id;
              const evals = r.evaluations || [];
              const avg = avgRating(evals);
              const salaryMats = r.salary_materials || [];
              const need = r.hiring_need;
              return (
                <div key={r.id} className={clsx(
                  'bg-white rounded-xl border overflow-hidden transition-all',
                  r.status === 'pending' ? 'border-gray-200 shadow-sm' : 'border-gray-200 hover:shadow-sm'
                )}>
                  {/* Header row */}
                  <div className="px-5 py-4 flex items-center gap-4 cursor-pointer" onClick={() => {
                      const next = expanded ? null : r.id;
                      setExpandedId(next);
                      if (next && evals.length > 0 && !evalSummaries[r.candidate_id]) fetchEvalSummary(r.candidate_id);
                    }}>
                    {/* Avatar */}
                    <div className={clsx(
                      'w-11 h-11 rounded-full flex items-center justify-center text-sm font-bold shrink-0',
                      r.photo_url ? '' : 'bg-primary-100 text-primary-600'
                    )}>
                      {r.photo_url
                        ? <img src={r.photo_url} alt="" className="w-11 h-11 rounded-full object-cover" />
                        : (r.candidate_name || '?').charAt(0)
                      }
                    </div>
                    {/* Main info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-bold text-gray-800">{r.candidate_name}</span>
                        <span className={clsx('inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-medium', sc.bg, sc.color)}>
                          <StatusIcon size={10} />{sc.label}
                        </span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{STEP_LABELS[r.step] || r.step}</span>
                        {r.source === 'node' && r.status === 'pending' && r.current_node_label && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary-50 text-primary-600">当前：{r.current_node_label}</span>
                        )}
                        {r.source === 'node' && r.awaiting_attachment && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-900 text-white">待HR上传附件</span>
                        )}
                      </div>
                      <div className="text-[11px] text-gray-400 mt-1 flex items-center gap-2 flex-wrap">
                        {r.position_name && <span className="font-medium text-gray-500">{r.position_name}</span>}
                        {r.department && <span>· {r.department}{r.sub_department ? ` / ${r.sub_department}` : ''}</span>}
                        {r.submitter_name && <span>· {r.submitter_name} 提交</span>}
                        <span>· {formatBeijingTime(r.created_at, 'datetime')}</span>
                        {evals.length > 0 && avg > 0 && (
                          <span className="inline-flex items-center gap-0.5 text-gray-500 font-medium">
                            <Star size={10} fill="currentColor" />{avg.toFixed(1)}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Actions */}
                    <div className="flex items-center gap-2 shrink-0">
                      {canNudge(r) && !canApprove(r) && (
                        <button onClick={e => { e.stopPropagation(); handleNudge(r.id); }} disabled={nudgingId === r.id}
                          className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 inline-flex items-center gap-1">
                          <Bell size={12} />{nudgingId === r.id ? '催办中...' : '催办'}
                        </button>
                      )}
                      {canApprove(r) && (
                        <>
                          <button onClick={e => { e.stopPropagation(); setApproveModal({ id: r.id, name: r.candidate_name, action: 'approved', source: r.source }); }}
                            className="px-3.5 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors shadow-sm">
                            通过
                          </button>
                          <button onClick={e => { e.stopPropagation(); setApproveModal({ id: r.id, name: r.candidate_name, action: 'rejected', source: r.source }); }}
                            className="px-3.5 py-1.5 text-xs font-medium text-gray-900 bg-white border border-gray-900 hover:bg-gray-900 hover:text-white rounded-lg transition-colors">
                            拒绝
                          </button>
                        </>
                      )}
                      <ChevronDown size={14} className={clsx('text-gray-300 transition-transform', expanded && 'rotate-180')} />
                    </div>
                  </div>

                  {/* ======== Expanded Decision Board ======== */}
                  {expanded && (
                    <div className="border-t border-gray-100 px-5 pb-5">

                      {/* Section 1: 需求背景 */}
                      {(need || r.position_name) && (
                        <div className="mt-4">
                          <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 mb-2">
                            <Target size={12} className="text-primary-500" />
                            需求背景
                          </div>
                          <div className="bg-primary-50/50 rounded-lg p-3 space-y-1.5 text-xs">
                            <div className="flex items-center gap-3 flex-wrap">
                              <span className="font-semibold text-gray-800">{r.position_name}</span>
                              {r.department && <span className="text-gray-500">· {r.department}{r.sub_department ? ` / ${r.sub_department}` : ''}</span>}
                              {need?.headcount && <span className="text-gray-500">· 招 {need.headcount} 人</span>}
                              {need?.urgency && (
                                <span className={clsx('px-1.5 py-0.5 rounded text-[10px] font-medium', URGENCY_LABELS[need.urgency]?.color)}>
                                  {URGENCY_LABELS[need.urgency]?.label || need.urgency}
                                </span>
                              )}
                            </div>
                            {need?.business_problem && (
                              <div className="text-gray-600 leading-relaxed">{need.business_problem}</div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Section 2: 面试评价 (AI Summary) */}
                      {evals.length > 0 && (
                        <div className="mt-4">
                          <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 mb-2">
                            <Users size={12} className="text-primary-500" />
                            面试评价
                            {avg > 0 && (
                              <span className="ml-1 inline-flex items-center gap-0.5 text-gray-500 font-bold text-[11px]">
                                <Star size={10} fill="currentColor" />{avg.toFixed(1)}/10
                              </span>
                            )}
                            <span className="text-[10px] text-gray-400 font-normal ml-1">({evals.length}位面试官)</span>
                          </div>

                          {/* Compact score badges row */}
                          <div className="flex flex-wrap gap-1.5 mb-2.5">
                            {evals.map((ev: any, i: number) => {
                              const roundLabel = ROUND_LABELS[ev.stage] || ev.round || ev.stage || '评价';
                              const resultColor = ev.result === 'passed' ? 'bg-primary-50 text-primary-700 border-primary-200'
                                : ev.result === 'rejected' ? 'bg-gray-50 text-gray-600 border-gray-200'
                                : 'bg-gray-50 text-gray-500 border-gray-200';
                              return (
                                <span key={i} className={clsx('inline-flex items-center gap-1 px-2 py-1 rounded-md border text-[10px] font-medium', resultColor)}>
                                  <span className="text-[10px] opacity-70">{roundLabel}</span>
                                  <span className="font-semibold">{ev.evaluator_name || '未知'}</span>
                                  {ev.rating > 0 && (
                                    <span className="inline-flex items-center gap-px text-gray-500">
                                      <Star size={8} fill="currentColor" />{ev.rating}
                                    </span>
                                  )}
                                </span>
                              );
                            })}
                          </div>

                          {/* AI Summary */}
                          <div className="bg-primary-50/60 rounded-lg p-3">
                            <div className="flex items-center gap-1 text-[10px] text-primary-500 font-medium mb-1.5">
                              <Sparkles size={10} />
                              AI 综合评价摘要
                            </div>
                            {summaryLoading[r.candidate_id] ? (
                              <div className="flex items-center gap-2 text-xs text-gray-400">
                                <div className="w-3 h-3 rounded-full border-2 border-primary-400 border-t-transparent animate-spin" />
                                正在生成摘要...
                              </div>
                            ) : (
                              <div className="text-xs text-gray-700 leading-relaxed">
                                {evalSummaries[r.candidate_id] || '展开后自动生成AI摘要...'}
                              </div>
                            )}
                          </div>

                          {/* Link to full detail */}
                          <button onClick={() => onOpenCandidate?.(r.candidate_id)}
                            className="mt-2 text-[11px] text-primary-500 hover:text-primary-700 hover:underline flex items-center gap-1">
                            <Eye size={11} />查看完整面试评价详情
                          </button>
                        </div>
                      )}

                      {/* Section 3: 薪酬材料（可在审批页直接上传/删除） */}
                      <div className="mt-4">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 mb-2">
                          <FileText size={12} className="text-primary-500" />
                          薪酬材料 {salaryMats.length > 0 && `(${salaryMats.length})`}
                        </div>

                        {isHr && (
                          <div className="bg-primary-50/50 rounded-lg p-3 mb-2 flex items-center gap-2 flex-wrap">
                            <input
                              type="file"
                              multiple
                              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.zip,.rar"
                              onChange={e => setSalFiles(prev => ({ ...prev, [r.candidate_id]: Array.from(e.target.files || []) }))}
                              className="flex-1 min-w-[180px] text-xs text-gray-500 file:mr-2 file:py-1 file:px-2.5 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-primary-100 file:text-primary-700 hover:file:bg-primary-200"
                            />
                            <button
                              disabled={!(salFiles[r.candidate_id]?.length) || salUploading === r.candidate_id}
                              onClick={() => handleSalaryUpload(r.candidate_id)}
                              className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 rounded-lg transition-colors">
                              {salUploading === r.candidate_id ? '上传中...' : '上传'}
                            </button>
                            {!!(salFiles[r.candidate_id]?.length) && (
                              <span className="text-[11px] text-gray-500 w-full">已选 {salFiles[r.candidate_id].length} 个文件</span>
                            )}
                          </div>
                        )}

                        {salaryMats.length === 0 ? (
                          <div className="text-xs text-gray-400 italic">暂无薪酬材料</div>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {salaryMats.map((m: any) => {
                              const isPdf = /\.pdf$/i.test(m.file_name);
                              const isExcel = /\.(xls|xlsx)$/i.test(m.file_name);
                              const isImg = /\.(jpg|jpeg|png|gif|webp)$/i.test(m.file_name);
                              return (
                                <div key={m.id} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors text-xs text-gray-700">
                                  <a href={m.file_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5">
                                    <span className={clsx('text-[10px] font-bold',
                                      isPdf ? 'text-gray-500' : isExcel ? 'text-primary-600' : isImg ? 'text-primary-500' : 'text-gray-400'
                                    )}>
                                      {isPdf ? 'PDF' : isExcel ? 'XLS' : isImg ? 'IMG' : 'FILE'}
                                    </span>
                                    <span className="truncate max-w-[150px]">{m.file_name}</span>
                                    <Download size={10} className="text-gray-400 shrink-0" />
                                  </a>
                                  {isHr && (
                                    <button onClick={() => handleSalaryDelete(m.id)}
                                      className="ml-1 text-gray-300 hover:text-gray-500" title="删除">
                                      <XCircle size={12} />
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* Section 4: 薪酬方案 & 录用理由 */}
                      <div className="mt-4">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 mb-2">
                          <MessageSquare size={12} className="text-gray-500" />
                          {r.step === 'pdf' ? 'Offer附件' : '薪酬方案 & 录用理由'}
                        </div>
                        <div className="bg-gray-50/50 rounded-lg p-3 space-y-2">
                          {r.reason && (
                            <div className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed">{r.reason}</div>
                          )}
                          {r.offer_image && (
                            /\.pdf$/i.test(r.offer_image) ? (
                              <a href={r.offer_image} target="_blank" rel="noreferrer"
                                className="inline-flex items-center gap-1.5 text-xs text-gray-600 hover:underline font-medium">
                                <FileText size={12} />查看方案PDF
                              </a>
                            ) : (
                              <img src={r.offer_image} alt="薪酬方案" 
                                className="max-w-md rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                                onClick={() => window.open(r.offer_image, '_blank')} />
                            )
                          )}
                          {r.offer_pdf && (
                            <a href={r.offer_pdf} target="_blank" rel="noreferrer"
                              className="inline-flex items-center gap-1.5 text-xs text-primary-600 hover:underline font-medium">
                              <FileText size={12} />查看Offer PDF
                            </a>
                          )}
                          {!r.reason && !r.offer_image && !r.offer_pdf && (
                            <div className="text-xs text-gray-400 italic">未提供方案说明</div>
                          )}
                        </div>
                      </div>

                      {/* Section 4.5: Offer 内容明细（HR 发起时填写的结构化内容，按权限脱敏展示） */}
                      <OfferDetailGrid o={r} />

                      {/* Section 5: 候选人画像 */}
                      <div className="mt-4">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 mb-2">
                          <Briefcase size={12} className="text-primary-500" />
                          候选人画像
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 text-xs bg-primary-50/40 rounded-lg p-3">
                          {r.school && (
                            <div className="flex items-center gap-1.5">
                              <GraduationCap size={11} className="text-gray-400 shrink-0" />
                              <span className="text-gray-500">学校:</span>
                              <span className="font-medium text-gray-700 truncate">{r.school}</span>
                            </div>
                          )}
                          {r.education && (
                            <div className="flex items-center gap-1.5">
                              <GraduationCap size={11} className="text-gray-400 shrink-0" />
                              <span className="text-gray-500">学历:</span>
                              <span className="font-medium text-gray-700">{r.education}</span>
                            </div>
                          )}
                          {r.work_years > 0 && (
                            <div className="flex items-center gap-1.5">
                              <Briefcase size={11} className="text-gray-400 shrink-0" />
                              <span className="text-gray-500">工龄:</span>
                              <span className="font-medium text-gray-700">{r.work_years}年</span>
                            </div>
                          )}
                          {r.latest_company && (
                            <div className="flex items-center gap-1.5">
                              <Building2 size={11} className="text-gray-400 shrink-0" />
                              <span className="text-gray-500">上家:</span>
                              <span className="font-medium text-gray-700 truncate">{r.latest_company}</span>
                            </div>
                          )}
                          {r.latest_title && (
                            <div className="flex items-center gap-1.5">
                              <Briefcase size={11} className="text-gray-400 shrink-0" />
                              <span className="text-gray-500">职位:</span>
                              <span className="font-medium text-gray-700 truncate">{r.latest_title}</span>
                            </div>
                          )}
                          {r.expected_salary && (
                            <div className="flex items-center gap-1.5">
                              <span className="text-gray-400 shrink-0 text-[10px]">💰</span>
                              <span className="text-gray-500">期望:</span>
                              <span className="font-bold text-gray-600">{r.expected_salary}</span>
                            </div>
                          )}
                          {r.expected_city && (
                            <div className="flex items-center gap-1.5">
                              <MapPin size={11} className="text-gray-400 shrink-0" />
                              <span className="text-gray-500">期望城市:</span>
                              <span className="font-medium text-gray-700">{r.expected_city}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Already approved info */}
                      {r.approver_name && r.status !== 'pending' && (
                        <div className={clsx('mt-4 rounded-lg p-3 flex items-start gap-2 text-xs',
                          r.status === 'approved' ? 'bg-primary-50' : 'bg-gray-50'
                        )}>
                          <AlertCircle size={13} className={r.status === 'approved' ? 'text-primary-500 mt-0.5 shrink-0' : 'text-gray-500 mt-0.5 shrink-0'} />
                          <div>
                            <span className="font-medium text-gray-700">审批人: {r.approver_name}</span>
                            {r.approved_at && <span className="text-gray-500 ml-1.5">{formatBeijingTime(r.approved_at, 'datetime')}</span>}
                            {r.approver_comment && <div className="text-gray-600 mt-1">"{r.approver_comment}"</div>}
                          </div>
                        </div>
                      )}

                      {/* Bottom actions */}
                      <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
                        <button onClick={() => onOpenCandidate?.(r.candidate_id)}
                          className="text-xs text-primary-600 hover:underline flex items-center gap-1">
                          <Eye size={12} />查看候选人完整详情
                        </button>
                        {canApprove(r) && (
                          <div className="flex items-center gap-2">
                            <button onClick={() => setApproveModal({ id: r.id, name: r.candidate_name, action: 'approved' })}
                              className="px-4 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors shadow-sm">
                              ✅ 通过
                            </button>
                            <button onClick={() => setApproveModal({ id: r.id, name: r.candidate_name, action: 'rejected' })}
                              className="px-4 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors">
                              ❌ 拒绝
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Approve/Reject Modal */}
      {approveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setApproveModal(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
              {approveModal.action === 'approved'
                ? <><CheckCircle size={18} className="text-primary-600" /> 通过 Offer</>
                : <><XCircle size={18} className="text-gray-500" /> 拒绝 Offer</>
              }
            </h3>
            <p className="text-sm text-gray-500 mt-1">候选人: {approveModal.name}</p>
            <textarea value={comment} onChange={e => setComment(e.target.value)}
              placeholder="审批意见（可选）"
              className="w-full mt-3 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              rows={3} />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setApproveModal(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">取消</button>
              <button onClick={handleApprove} disabled={processing}
                className={clsx('px-4 py-2 text-sm font-medium rounded-lg text-white disabled:opacity-50',
                  approveModal.action === 'approved' ? 'bg-primary-600 hover:bg-primary-700' : 'bg-gray-500 hover:bg-gray-600')}>
                {processing ? '处理中...' : approveModal.action === 'approved' ? '确认通过' : '确认拒绝'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
